/**
 * FireAlarmProvider — exposes the live fire-alarm state to the shell
 * and renders the audio + visual safety response.
 *
 * - Subscribes to the `['fire_alarm', siteId]` cache (kept live by
 *   `useLiveData` from `rmf/v1/sites/{site}/emergency/fire/state`) AND
 *   to the api-server's own `GET .../emergency/fire`, preferring
 *   whichever of the two this browser learned about most recently.
 *
 *   THE SECOND SOURCE IS NOT REDUNDANT. The live key is stored, but a
 *   storage is not a replay: the Zenoh REST/SSE plugin serves a
 *   streaming request by declaring a subscriber, and a subscriber only
 *   ever sees samples published AFTER it exists. Fire mode publishes on
 *   transitions — trigger and resolve — so a browser opened five
 *   minutes into an evacuation subscribed to a key that had already
 *   said everything it was going to say, and rendered a calm building.
 *   Every other category in this app already solves this with a REST
 *   seed (`robotsQueryOptions`, `tasksQueryOptions`,
 *   `fetchCategorySamples`); this is fire mode's.
 *
 *   "Most recently" is measured in BROWSER time on both sides —
 *   TanStack's `dataUpdatedAt` for each — never by comparing the live
 *   sample's server-stamped `unix_millis_time` against a local clock.
 *   Cross-clock comparison between a hotel's api-server and an
 *   operator's laptop is exactly the kind of thing that silences an
 *   alarm when the two drift.
 * - Plays a siren when the alarm transitions idle → triggered, and
 *   stops the siren when it clears.
 * - Audio behaviour honours the user's fire-alarm-audio preference
 *   (`always` / `mute-with-rest` / `never`). The visual overlay is
 *   never suppressed — operators must see the alarm even if audio
 *   is muted.
 * - Registers the default Web Audio siren factory on mount and
 *   removes it on unmount.
 * - Exposes `{ isFireAlarmActive, fireAlarmTriggeredAt, fireAlarm }`
 *   to descendants via context.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSafeSiteId } from './useActiveSite';
import { fireAlarmKey } from '../api/scope';
import { adaptFireAlarm, type FireAlarmViewModel } from '../api/adapters';
import { useFireModeState } from '../api/hooks/useFireMode';
import { FireModePanel } from '../screens/Emergency/FireModePanel';
import { FireAlarmContext, type FireAlarmContextValue } from './fire-alarm.context';
import type { FireModeState } from '../api/v1';
import { getSirenFactory, setSirenFactory } from './fire-alarm.siren';
import { defaultSirenFactory } from './fire-alarm.siren.default';
import { isMuted, subscribeMute } from './alert-audio';
import { useUserPreferences } from './useUserPreferences';

const DEFAULT_VIEW: FireAlarmViewModel = { state: 'unknown', triggeredAt: null };

/** Decide whether the siren should play given the user's preference
 *  and the current master mute state. The `muted` flag is passed in
 *  (rather than read from `isMuted()` synchronously inside the
 *  effect) so the effect's dependency array can include it and the
 *  siren reacts to mute toggles mid-event. */
function shouldPlaySiren(
  mode: 'always' | 'mute-with-rest' | 'never',
  muted: boolean,
): boolean {
  if (mode === 'never') return false;
  if (mode === 'always') return true;
  // mute-with-rest
  return !muted;
}

/** A live sample, plus WHEN THIS BROWSER received it. The timestamp is
 *  the only thing that can be compared against the REST bootstrap
 *  without crossing clocks. `updatedAt: 0` means "no sample yet", which
 *  is distinct from a sample that says the building is clear. */
interface LiveRead {
  view: FireAlarmViewModel;
  updatedAt: number;
}

const NO_LIVE_READ: LiveRead = { view: DEFAULT_VIEW, updatedAt: 0 };

export function FireAlarmProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  const { prefs } = useUserPreferences();
  const [live, setLive] = useState<LiveRead>(NO_LIVE_READ);
  const [panelOpen, setPanelOpen] = useState(false);
  const [siren, setSiren] = useState<{ stop: () => void } | null>(null);
  const [muted, setMutedState] = useState<boolean>(() => isMuted());

  const bootstrap = useFireModeState();
  const bootstrapAt = bootstrap.data !== undefined ? bootstrap.dataUpdatedAt : 0;

  // Whichever source this browser heard from last. A stale REST read
  // must not re-assert an alarm the stream has since cleared, and a
  // stream that has said nothing since the page loaded must not hide an
  // alarm the REST read found — so this is a comparison, not a
  // precedence rule.
  const view: FireAlarmViewModel =
    bootstrapAt > live.updatedAt && bootstrap.data !== undefined
      ? fireModeStateToView(bootstrap.data)
      : live.view;

  // Register the default siren factory on first mount if no custom
  // factory is already registered (e.g. from a test). Tests can call
  // `setSirenFactory(null)` to suppress audio.
  useEffect(() => {
    if (getSirenFactory() === null) {
      setSirenFactory(defaultSirenFactory);
    }
    return () => {
      setSirenFactory(null);
      setSiren((s) => {
        s?.stop();
        return null;
      });
    };
  }, []);

  // Reset on site change.
  useEffect(() => {
    setLive(NO_LIVE_READ);
    setPanelOpen(false);
    setSiren((s) => {
      s?.stop();
      return null;
    });
  }, [siteId]);

  // Subscribe to the fire_alarm cache. We re-read on every cache
  // update that touches our key.
  useEffect(() => {
    function read(): LiveRead {
      const state = qc.getQueryState(fireAlarmKey(siteId));
      if (!state || state.data === undefined) return NO_LIVE_READ;
      return { view: adaptFireAlarm(state.data), updatedAt: state.dataUpdatedAt };
    }
    setLive(read());
    const unsubscribe = qc.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      const k = event.query.queryKey;
      if (k[0] !== 'fire_alarm' || k[1] !== siteId) return;
      setLive(read());
    });
    return () => {
      unsubscribe();
    };
  }, [qc, siteId]);

  // Subscribe to master mute changes so the siren can be started
  // or stopped mid-alarm when the user flips the toggle.
  useEffect(() => subscribeMute(() => setMutedState(isMuted())), []);

  // Audio side effect: start/stop the siren on transitions. The
  // user's fireAlarmAudio preference is honoured here. When the
  // master mute toggle or the preference changes mid-event we
  // react by stopping/starting the siren so the new setting takes
  // effect immediately.
  useEffect(() => {
    const active = view.state === 'triggered';
    const playEnabled = shouldPlaySiren(prefs.fireAlarmAudio, muted);
    if (active && playEnabled && !siren) {
      const factory = getSirenFactory();
      const handle = factory?.();
      if (handle) setSiren({ stop: handle.stop });
    } else if (!active && siren) {
      siren.stop();
      setSiren(null);
    } else if (active && !playEnabled && siren) {
      // Preference changed to "never" or master mute toggled on
      // while the alarm is active: stop the siren immediately.
      siren.stop();
      setSiren(null);
    }
  }, [view.state, prefs.fireAlarmAudio, siren, muted]);

  const openFireModePanel = useCallback(() => setPanelOpen(true), []);

  const value: FireAlarmContextValue = {
    isFireAlarmActive: view.state === 'triggered',
    fireAlarmTriggeredAt: view.triggeredAt,
    fireAlarm: view,
    openFireModePanel,
  };

  return (
    <FireAlarmContext.Provider value={value}>
      {children}
      {/* Mounted only while open, so the panel's own queries never run
          for the overwhelming majority of sessions in which nobody
          opens it. */}
      {panelOpen && <FireModePanel onClose={() => setPanelOpen(false)} />}
    </FireAlarmContext.Provider>
  );
}

/** The api-server's `FireModeState` in the shape the overlay, banner
 *  and siren already consume. `triggered_at_ms` is what the overlay
 *  prints, and is the same instant the live sample carries as
 *  `unix_millis_time` on the trigger publish — so a page loaded during
 *  an alarm shows the same timestamp as one that was open for it. */
function fireModeStateToView(state: FireModeState): FireAlarmViewModel {
  if (!state.active) return { state: 'idle', triggeredAt: null, siteId: state.site };
  const triggeredAt = state.event?.triggered_at_ms ?? null;
  return {
    state: 'triggered',
    triggeredAt,
    lastSeen: triggeredAt ? new Date(triggeredAt).toISOString() : undefined,
    siteId: state.site,
  };
}
